import Layout from '@src/components/layout';
import { initAmplitude } from '@src/functions/amplitude';
import { captureAttribution } from '@src/functions/attribution';
import { setGclid } from '@src/functions/gclid';
import '@src/styles/globals.css';
import '@src/styles/Wordpress.css'
import '@n8n/chat/style.css'
import '@src/styles/n8n-chat-theme.css'
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React from 'react';


export default function App({ Component, pageProps }: AppProps) {
  const env = process.env.NEXT_PUBLIC_ENV;
  const router = useRouter();
  const { gclid } = router.query;

  setGclid(gclid as string);

  // Paid traffic lands here with gclid/utm_* on the URL, but the booking app is
  // a separate origin that can only read them off a link's query string. Stash
  // them for the session so a visitor who browses on before clicking a CTA
  // still arrives attributed — every link into the app is stamped from this
  // record. See src/functions/attribution.ts.
  React.useEffect(() => {
    captureAttribution();
  }, [router.asPath]);

  // Amplitude is the site's analytics destination. Loaded here in an effect (not
  // as a <Script>) so its SDK lands in a lazy chunk rather than the initial JS of
  // every page; autocapture picks up page views, sessions and utm attribution
  // from this point on. Events fired before it resolves are queued, so a CTA
  // clicked immediately still reports. See src/functions/amplitude.ts.
  React.useEffect(() => {
    initAmplitude();
  }, []);

  return (
    <>
      <Script id="data-layer" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({originalLocation: document.location.protocol + '//' + document.location.hostname + document.location.pathname + document.location.search});
        `}
      </Script>
      {env === "production" && (
        <>
          <Script id="google-tag-manager" strategy="afterInteractive">
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');
            `}
          </Script>
          <Script id="kustomer" strategy="afterInteractive">
            {`
              window.addEventListener('kustomerLoaded', function() {
                Kustomer.start({
                  brandId: '${process.env.NEXT_PUBLIC_KUSTOMER_BRAND_ID}'
                });
              });
              var script = document.createElement('script');
              script.src = 'https://cdn.kustomerapp.com/chat-web/widget.js';
              script.setAttribute('data-kustomer-api-key', '${process.env.NEXT_PUBLIC_KUSTOMER_API_KEY}');
              window.document.body.appendChild(script);`
            }
          </Script>
          <Script id="use-proof" src={`https://cdn.useproof.com/proof.js?acc=${process.env.NEXT_PUBLIC_USE_PROOF_ID}`} strategy="afterInteractive" />
        </>
      )}
      <Script id="trust-pilot" src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" strategy="afterInteractive" />
      <Layout>
        <Component {...pageProps} />
      </Layout>
    </>
  )
}
